/**
 * DiscoveryModule — the Discovery tab (P11): the four surfaces a player uses to
 * find worlds.
 *
 *   • "What's Hot" (40%)   — the server-materialized algorithmic `feedRank`.
 *   • "Your Orbit" (40%)   — derived client-side from validated Orbits + vouches.
 *   • "What's On" (20%)    — upcoming events (P8).
 *   • Patronage Board      — supported creators (P10), always-on, bypasses the algo.
 *
 * The 40/40/20 blend is assembled by the shared pure helper `lib/feed.ts`
 * (`assembleHomeFeed`), which can NEVER collapse to algorithm-only — an empty
 * surface shows an empty state, its slots are never handed to the algorithm. Plus
 * a "Rate this world" control and the creator algorithmic opt-out. The client only
 * RENDERS replicated rows + CALLS reducers; every score is server-computed.
 */

import type { App, IModule } from 'enginex';
import type { DbConnection } from '../module_bindings/index.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import {
  assembleHomeFeed,
  algoWorlds,
  deriveSocialWorlds,
  upcomingEvents,
  patronageCreators,
  type BlendedFeed,
} from '../lib/feed.js';

export class DiscoveryModule implements IModule {
  readonly name = 'Discovery';

  private bound = false;
  private els: Record<string, HTMLElement> = {};

  init(_app: App): void {
    for (const id of ['disc-feed', 'disc-patronage', 'disc-optout', 'disc-optout-note', 'disc-rate', 'disc-rate-note']) {
      const el = document.getElementById(id);
      if (el) this.els[id] = el;
    }
    bus.on('atlas:account-ready', () => {
      this.bind();
      this.render();
    });
    // Any source the feed reads can change the blend.
    for (const ev of ['atlas:exchange-updated', 'atlas:reputation-updated', 'atlas:orbit-updated', 'atlas:events-updated', 'atlas:world-entered'] as const) {
      bus.on(ev, () => this.render());
    }
    this.wireOptOut();
    this.render();
  }

  /** Re-render whenever a discovery table changes. */
  private bind(): void {
    if (this.bound) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.bound = true;
    const ping = () => this.render();
    for (const tbl of [conn.db.feedRank, conn.db.worldMetric, conn.db.worldRating, conn.db.discoveryPrefs]) {
      const t = tbl as unknown as { onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void };
      t.onInsert(ping);
      t.onUpdate(ping);
      t.onDelete(ping);
    }
  }

  private wireOptOut(): void {
    const box = this.els['disc-optout'] as HTMLInputElement | undefined;
    box?.addEventListener('change', () => {
      const conn = atlas.conn;
      if (!conn) return;
      this.note('disc-optout-note', 'Updating…', '');
      void conn.reducers
        .setDiscoveryPrefs({ algoOptOut: box.checked })
        .then(() => this.note('disc-optout-note', box.checked ? 'Opted OUT of algorithmic surfacing — you live on invites + patronage.' : 'Algorithmic surfacing ON.', 'ok'))
        .catch((e: Error) => this.note('disc-optout-note', e.message, 'err'));
    });
  }

  private render(): void {
    const conn = atlas.conn;
    if (!conn || !conn.identity) return;
    const viewer = conn.identity;
    this.renderFeed(conn, viewer);
    this.renderPatronage(conn, viewer);
    this.renderOptOut(conn, viewer);
    this.renderRate(conn);
  }

  private worldName(conn: DbConnection, id: bigint): string {
    const w = conn.db.world.worldId.find(id);
    return this.esc(w ? w.name : `world #${id}`);
  }

  private renderFeed(conn: DbConnection, viewer: import('spacetimedb').Identity): void {
    const el = this.els['disc-feed'];
    if (!el) return;
    const nowMicros = BigInt(Date.now()) * 1_000n;
    const blend: BlendedFeed = assembleHomeFeed(
      { algo: algoWorlds(conn), social: deriveSocialWorlds(conn, viewer), events: upcomingEvents(conn, nowMicros) },
      10,
    );

    const worldRow = (id: bigint): string => {
      const m = conn.db.worldMetric.worldId.find(id);
      const meta = m ? `★${m.avgRating.toFixed(1)} · hot ${m.hotScore.toFixed(1)}` : 'new';
      return `<div class="market-item"><div class="head"><span class="kind">${this.worldName(conn, id)}</span><span class="price">${meta}</span></div></div>`;
    };
    const eventRow = (id: bigint): string => {
      const e = conn.db.event.eventId.find(id);
      return `<div class="market-item"><div class="head"><span class="kind">${this.esc(e ? e.title : `event #${id}`)}</span><span class="price">event</span></div></div>`;
    };
    const section = (title: string, body: string, empty: string): string =>
      `<h3>${title}</h3>${body || `<div class="empty">${empty}</div>`}`;

    el.innerHTML =
      section('What’s Hot · 40%', blend.algo.map(worldRow).join(''), 'No hot worlds yet.') +
      section('Your Orbit · 40%', blend.social.map(worldRow).join(''), 'No worlds from your Orbit or vouches yet — visit friends or vouch for a creator.') +
      section('What’s On · 20%', blend.events.map(eventRow).join(''), 'No upcoming events.');
  }

  private renderPatronage(conn: DbConnection, viewer: import('spacetimedb').Identity): void {
    const el = this.els['disc-patronage'];
    if (!el) return;
    const names = new Map<string, string>();
    for (const a of conn.db.account.iter()) names.set(a.identity.toHexString(), a.displayName || a.username);
    const creators = patronageCreators(conn, viewer);
    if (creators.length === 0) {
      el.innerHTML = '<div class="empty">You support no creators yet. Patronage appears here, always — bypassing the algorithm.</div>';
      return;
    }
    el.innerHTML = creators
      .map((hex) => `<div class="market-item"><div class="head"><span class="kind">${this.esc(names.get(hex) ?? hex.slice(0, 10) + '…')}</span><span class="price">patron</span></div></div>`)
      .join('');
  }

  private renderOptOut(conn: DbConnection, viewer: import('spacetimedb').Identity): void {
    const box = this.els['disc-optout'] as HTMLInputElement | undefined;
    if (!box) return;
    const prefs = conn.db.discoveryPrefs.creator.find(viewer);
    box.checked = prefs ? prefs.algoOptOut : false;
  }

  private renderRate(conn: DbConnection): void {
    const el = this.els['disc-rate'];
    if (!el) return;
    const worldId = atlas.currentWorldId;
    if (worldId === null) {
      el.innerHTML = '<div class="empty">Enter a world to rate it.</div>';
      return;
    }
    const m = conn.db.worldMetric.worldId.find(worldId);
    const cur = m && m.ratingCount > 0 ? `current ★${m.avgRating.toFixed(1)} (${m.ratingCount})` : 'unrated';
    el.innerHTML =
      `<div class="meta">Rate ${this.worldName(conn, worldId)} — ${cur}</div>` +
      `<div class="actions">${[1, 2, 3, 4, 5].map((s) => `<button data-stars="${s}" class="action secondary">${s}★</button>`).join('')}</div>`;
    for (const s of [1, 2, 3, 4, 5]) {
      el.querySelector(`[data-stars="${s}"]`)?.addEventListener('click', () => this.rate(worldId, s));
    }
  }

  private rate(worldId: bigint, stars: number): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.note('disc-rate-note', `Rating ${stars}★…`, '');
    void conn.reducers
      .rateWorld({ worldId, stars })
      .then(() => this.note('disc-rate-note', `Rated ${stars}★. Discovery recomputes the world’s standing.`, 'ok'))
      .catch((e: Error) => this.note('disc-rate-note', e.message, 'err'));
  }

  private note(id: string, text: string, cls: '' | 'ok' | 'err'): void {
    const el = this.els[id];
    if (!el) return;
    el.className = cls ? `note ${cls}` : 'note';
    el.textContent = text;
  }

  /** Escape user-controlled strings before interpolating into innerHTML (anti-XSS). */
  private esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) =>
      c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
    );
  }
}
